import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = process.env.REACT_APP_API_URL;

function normalizeBlogCtaPage(value) {
  const target = String(value || "").trim().toLowerCase();

  if (!target) return "";

  if (target === "fortunewheel") return "fortuneWheel";
  if (target === "hiddenseek") return "hiddenSeek";
  if (target === "arenaleaderboard") return "arenaLeaderboard";

  return target;
}

const CATEGORY_LABELS = {
  news: "News",
  update: "Update",
  instructions: "Instructions",
  event: "Event",
  maintenance: "Maintenance",
};

function formatDate(value) {
  if (!value) return "";

  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function categoryLabel(value) {
  const key = String(value || "").toLowerCase();
  return CATEGORY_LABELS[key] || key || "News";
}

function getPostImage(post) {
  const img = String(post?.image_url || post?.imageUrl || "").trim();
  return img || null;
}

function formatRewardText(reward) {
  const parts = [];

  const sparks = Number(reward?.sparks_amount || 0);
  const runes = Number(reward?.runes_amount || 0);

  if (sparks > 0) {
    parts.push(`${sparks} spark${sparks === 1 ? "" : "s"}`);
  }

  if (runes > 0) {
    parts.push(`${runes} rune${runes === 1 ? "" : "s"}`);
  }

  return parts.join(" + ");
}

function BlogRewardToast({ reward, visible, onClose }) {
  if (!reward || !visible) return null;

  const sparks = Number(reward?.sparks_amount || 0);
  const runes = Number(reward?.runes_amount || 0);
  const text = formatRewardText(reward);

  if (!text) return null;

  return (
    <AnimatePresence>
      <div
        className="modal_box"
        onClick={onClose}
        style={{ zIndex: 10050 }}
      >

      <div className="modal_title">WooHoo!<button onClick={onClose} className="modal_close"/></div>
        <motion.div
          className="modal_body"   initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
        <div className="modal_inside">
          <img
            src="/finger.png"
            alt="Success"
            style={{
              height: "80px",
              marginBottom: "10px",
            }}
          />

          <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: "15px" }}>
            Reward Claimed!
          </div>

          <div className="shadow1"
            style={{
              marginBottom: "10px",
              fontSize: "20px",
              fontWeight: 600
            }}
          >
            Your blog reward was added to your account!
          </div>

          <div className="info"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 12,
              marginBottom: 12,
            }}
          >
            {sparks > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#fff",
                  fontSize: 21,
                  textShadow: "1px 1px #000",
                }}
              >
                <img
                  src="/sparks.png"
                  alt=""
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: "contain",
                  }}
                />
                <strong>{sparks}</strong>
              </div>
            )}

            {runes > 0 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#fff",
                  fontSize: 28,
                  textShadow: "1px 1px #000",
                }}
              >
                <img
                  src="/runes.png"
                  alt=""
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: "contain",
                  }}
                />
                <strong>{runes}</strong>
              </div>
            )}
          </div>

          <div
          className="shadow1"
            style={{
              fontWeight: 600,
              fontSize: "14px",
              color: "rgba(255,255,255,0.85)",
              marginTop: 6,
            }}
          >
            Each article reward can be claimed once.
          </div>

          </div>

          <div className="modal_footer">
            <button className="game-button red-button" onClick={onClose}>
              AWESOME
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function renderBlogBody(body) {
  const text = String(body || "");

  return text.split(/(<h1>.*?<\/h1>)/gis).map((part, index) => {
    const match = part.match(/^<h1>(.*?)<\/h1>$/is);

    if (match) {
      return (
        <h1
          key={index}
          style={{
            fontSize: 28,
            lineHeight: "28px",
            color: "#ffdf5f",
            margin: "5px 0 5px",
            textShadow: "2px 2px 0 #2b1400",
          }}
        >
          {match[1]}
        </h1>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function BlogPostModal({
  post,
  onClose,
  onNavigate,
  rewardStatus,
  claiming,
  onClaimReward,
}) {
  const image = getPostImage(post);

  return (
    <AnimatePresence>
      {post && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            width: "100vw",
            height: "100dvh",
            background: "rgba(0,0,0,0.001)",
            touchAction: "none",
          }}
        >
          <motion.div
            initial={{ scale: 0.985, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.985, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            style={{
              position: "absolute",
              inset: 0,
              overflowY: "auto",
              backgroundColor: "#120812",
              backgroundImage: "url(/v2/main_bg.png)",
              backgroundSize: "100% auto",
              backgroundRepeat: "repeat-y",
              backgroundPosition: "center top",
              paddingTop: "calc(20px + var(--tg-safe-top))",
              paddingBottom: "max(28px, env(safe-area-inset-bottom))",
            }}
          >
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                position: "fixed",
                top: "calc(10px + var(--tg-safe-top))",
                right: "max(14px, env(safe-area-inset-right))",
                zIndex: 10000,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img
                src="/v2/close.png"
                alt="Close"
                style={{
                  width: 32,
                  height: 32,
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            </button>

            <div
              style={{
                width: "100%",
                maxWidth: 560,
                margin: "0 auto",
                boxSizing: "border-box",
                paddingLeft: 14,
                paddingRight: 14,
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 13px",
                    borderRadius: 999,
                    background: "rgba(0,0,0,0.35)",
                    border: "1px solid rgba(255,223,95,0.35)",
                    color: "#ffdf5f",
                    fontSize: 15,
                    textShadow: "1px 1px rgba(0,0,0,0.7)",
                  }}
                >
                  {categoryLabel(post.category)}
                  {post.published_at && (
                    <>
                      <span style={{ opacity: 0.6 }}>•</span>
                      <span>{formatDate(post.published_at)}</span>
                    </>
                  )}
                </div>

                <h1
                className="shadow2"
                  style={{
                    fontSize: 28,
                    color: "#fff",
                    margin: "14px 0 4px",
                  }}
                >
                  {post.title}
                </h1>

                {post.summary && (
                  <div
                    style={{
                      color: "rgba(255,255,255,0.88)",
                      fontSize: 16,
                      textShadow: "1px 1px rgba(0,0,0,0.65)",
                      marginTop: 10,
                      fontWeight: 600
                    }}
                  >
                    {post.summary}
                  </div>
                )}
              </div>

             {image && (
  <div
    style={{
      width: "100%",
      aspectRatio: "360 / 145",
      boxSizing: "border-box",
      borderRadius: 10,
      overflow: "hidden",
      border: "2px solid rgba(255,223,95,0.35)",
      boxShadow: "0 3px 0 rgba(0,0,0,0.28)",
      margin: "10px 0",
      background: "rgba(0,0,0,0.28)",
    }}
  >
    <img
      src={image}
      alt=""
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        objectFit: "cover",
      }}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  </div>
)}

              <div
                style={{
                  background: "rgba(0,0,0,0.35)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 10,
                  padding: "18px 16px",
                  color: "rgba(255,255,255,0.94)",
                  fontSize: 21,
                  lineHeight: "25px",
                  textShadow: "1px 1px rgba(0,0,0,0.65)",
                  whiteSpace: "pre-wrap",
                  boxShadow: "0 3px 0 rgba(0,0,0,0.2)",
                  textAlign: "left",
                  overflowWrap: "break-word",
                  fontWeight: 600
                }}
              >
                {renderBlogBody(post.body)}
              </div>

              <div style={{ textAlign: "center", marginTop: 26 }}>
                {!rewardStatus || rewardStatus.loading ? (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 58,
                      minWidth: 190,
                      borderRadius: 18,
                      padding: "0 18px",
                      background: "rgba(0,0,0,0.35)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      color: "rgba(255,255,255,0.75)",
                      fontSize: 26,
                      textShadow: "2px 2px rgba(0,0,0,0.65)",
                    }}
                  >
                    Checking...
                  </div>
                ) : rewardStatus.claimed || rewardStatus.already_claimed ? (
                  <div
                  className="game-button red-button"
                  >
                    CLAIMED
                  </div>
                ) : (
                  <button
                  className="game-button green-button"
                    type="button"
                    disabled={claiming}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClaimReward?.();
                    }}
                  >
                    {claiming ? "Claiming…" : "Claim Reward"}
                  </button>
                )}
              </div>

              {post.cta_label && post.cta_url && (
                <div style={{ textAlign: "center", marginTop: 26 }}>
                  <button
                  className="game-button yellow-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const page = normalizeBlogCtaPage(post.cta_url);
                      if (!page) return;

                      onNavigate?.(page);
                    }}
                    style={{
                    textTransform: "uppercase"  
                    }}
                  >
                    {post.cta_label}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Blog({ user, setUser, onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedPost, setSelectedPost] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [reward, setReward] = useState(null);
  const [rewardVisible, setRewardVisible] = useState(false);
  const [articleRewardStatus, setArticleRewardStatus] = useState({});
  const [claimingArticleSlug, setClaimingArticleSlug] = useState("");

  const categories = useMemo(() => {
    const found = new Set();

    posts.forEach((post) => {
      const cat = String(post?.category || "").trim().toLowerCase();
      if (cat) found.add(cat);
    });

    return ["all", ...Array.from(found)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (activeCategory === "all") return posts;

    return posts.filter(
      (post) => String(post?.category || "").toLowerCase() === activeCategory
    );
  }, [posts, activeCategory]);

  async function loadBlogArticleRewardStatus(post) {
    const token = localStorage.getItem("token");
    const slug = String(post?.slug || "").trim();

    if (!slug) return;

    setArticleRewardStatus((current) => ({
      ...current,
      [slug]: {
        loading: true,
        claimed: false,
        already_claimed: false,
      },
    }));

    if (!token) {
      setArticleRewardStatus((current) => ({
        ...current,
        [slug]: {
          loading: false,
          claimed: false,
          already_claimed: false,
        },
      }));

      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/blog/post-reward/status?slug=${encodeURIComponent(
          slug
        )}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to load reward status.");
      }

      const claimed = Boolean(data?.claimed || data?.already_claimed);

      setArticleRewardStatus((current) => ({
        ...current,
        [slug]: {
          loading: false,
          claimed,
          already_claimed: claimed,
        },
      }));
    } catch (err) {
      console.error("Blog article reward status failed:", err);

      setArticleRewardStatus((current) => ({
        ...current,
        [slug]: {
          loading: false,
          claimed: false,
          already_claimed: false,
        },
      }));
    }
  }

  async function claimBlogArticleReward(post) {
    const token = localStorage.getItem("token");
    const slug = String(post?.slug || "").trim();

    if (!token) {
      console.warn("Blog article reward skipped: NO_TOKEN");
      return;
    }

    if (!slug) {
      console.warn("Blog article reward skipped: NO_SLUG");
      return;
    }

    try {
      setClaimingArticleSlug(slug);

      const res = await fetch(`${API_BASE}/api/blog/post-reward/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const text = await res.text();

      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { raw: text };
      }

      if (!res.ok) {
        throw new Error(
          data?.error || data?.message || text || `HTTP ${res.status}`
        );
      }

      if (data?.already_claimed) {
        setArticleRewardStatus((current) => ({
          ...current,
          [slug]: {
            loading: false,
            claimed: true,
            already_claimed: true,
          },
        }));

        return;
      }

      if (data?.claimed) {
        const rewardSparks = Number(
          data?.sparks_amount ?? data?.reward?.sparks_amount ?? 0
        );

        const rewardRunes = Number(
          data?.runes_amount ?? data?.reward?.runes_amount ?? 0
        );

        const finalSparks =
          data?.balances?.sparks ??
          data?.balance?.sparks ??
          data?.user?.sparks ??
          null;

        const finalRunes =
          data?.balances?.runes ??
          data?.balance?.runes ??
          data?.user?.runes ??
          null;

        if (typeof setUser === "function") {
          setUser((prev) => ({
            ...(prev || {}),
            sparks:
              finalSparks != null
                ? Number(finalSparks)
                : Number(prev?.sparks ?? 0) + rewardSparks,
            runes:
              finalRunes != null
                ? Number(finalRunes)
                : Number(prev?.runes ?? 0) + rewardRunes,
          }));
        }

        setArticleRewardStatus((current) => ({
          ...current,
          [slug]: {
            loading: false,
            claimed: true,
            already_claimed: true,
          },
        }));

        setReward({
          ...data,
          sparks_amount: rewardSparks,
          runes_amount: rewardRunes,
        });

        setRewardVisible(true);

        window.dispatchEvent(
          new CustomEvent("blogArticleRewardClaimed", {
            detail: data,
          })
        );
      } else {
        await loadBlogArticleRewardStatus(post);
      }
    } catch (err) {
      console.error("Blog article reward claim failed:", err);
      await loadBlogArticleRewardStatus(post);
    } finally {
      setClaimingArticleSlug("");
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadBlogPosts() {
      try {
        setLoading(true);
        setError("");

        const language =
          String(user?.language || user?.lang || "").trim().toLowerCase() ||
          "en";

        const params = new URLSearchParams();
        params.set("language", language);
        params.set("limit", "50");

        const res = await fetch(`${API_BASE}/api/blog?${params.toString()}`, {
          headers: {
            Accept: "application/json",
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load blog.");
        }

        if (!cancelled) {
          setPosts(Array.isArray(data?.posts) ? data.posts : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load blog.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBlogPosts();

    return () => {
      cancelled = true;
    };
  }, [user?.language, user?.lang]);

  return (
    <div
      className="shop-container"
      style={{
        fontFamily: '"Fredoka", sans-serif',
        paddingTop: 0
      }}
    >
      <BlogRewardToast
        reward={reward}
        visible={rewardVisible}
        onClose={() => setRewardVisible(false)}
      />

      <div
        style={{
          width: "100%",
          maxWidth: 560,
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "relative",
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          <div
          className="shadow2"
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: "#fff"
            }}
          >
            Taptopia News
          </div>

          <div
          className="shadow1"
            style={{
              fontWeight: 600,
              marginTop: 5,
              fontSize: 18,
              color: "#fff"
            }}
          >
            News, Updates, Events and Instructions
          </div>
        </div>

       {categories.length > 2 && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))`,
      width: "100%",
      gap: 8,
      alignItems: "stretch",
      marginBottom: 12,
      overflowX: "auto",
      paddingBottom: 4,
    }}
  >
    {categories.map((cat) => {
      const active = activeCategory === cat;

      return (
        <button
          key={cat}
          type="button"
          onClick={() => setActiveCategory(cat)}
          style={{
            width: "100%",
            minWidth: 92,
            height: 46,
            padding: "0 6px",
            margin: 0,
            boxSizing: "border-box",

            background: active
              ? "rgba(112, 61, 27, 0.96)"
              : "rgba(95, 49, 18, 0.76)",

            border: active
              ? "2px solid rgba(135, 78, 36, 0.98)"
              : "2px solid rgba(70, 32, 10, 0.95)",

            borderRadius: 7,

            boxShadow: active
              ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
              : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

            color: "#fff",
            fontSize: 12,
            lineHeight: "42px",
            fontWeight: 600,
            textAlign: "center",
            textTransform: "uppercase",

            textShadow:
              "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",

            cursor: "pointer",
            appearance: "none",
            WebkitAppearance: "none",
            opacity: active ? 1 : 0.88,
            fontFamily: '"Fredoka", sans-serif',
          }}
        >
          {cat === "all" ? "All" : categoryLabel(cat)}
        </button>
      );
    })}
  </div>
)}

        {loading && (
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              padding: "60px 0",
              fontSize: 20,
              textShadow: "1px 1px rgba(0,0,0,0.7)",
            }}
          >
            Loading news…
          </div>
        )}

        {!loading && error && (
          <div
            style={{
              background: "rgba(255,107,107,0.16)",
              border: "1px solid rgba(255,107,107,0.45)",
              color: "#fff",
              borderRadius: 18,
              padding: 16,
              textAlign: "center",
              textShadow: "1px 1px rgba(0,0,0,0.7)",
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && filteredPosts.length === 0 && (
          <div
            style={{
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.9)",
              borderRadius: 18,
              padding: 18,
              textAlign: "center",
              textShadow: "1px 1px rgba(0,0,0,0.7)",
            }}
          >
            No news yet. Check back soon!
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {!loading &&
            !error &&
            filteredPosts.map((post, index) => {
              const image = getPostImage(post);

              return (
                <motion.button
                  key={post.id || post.slug || index}
                  type="button"
                  onClick={() => {
                    setSelectedPost(post);
                    loadBlogArticleRewardStatus(post);
                  }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.035, 0.2) }}
                  style={{
                    width: "100%",
                    display: "block",
                    textAlign: "left",
                    border: "1px solid rgba(255,255,255,0.13)",
                    borderRadius: 10,
                    overflow: "hidden",
                    padding: 0,
                    cursor: "pointer",
                    background: "#977148",
                    boxShadow: "0 3px 0 rgba(0,0,0,0.22)",
                  }}
                >
                  {image && (
  <div
    style={{
      width: "100%",
      aspectRatio: "360 / 145",
      boxSizing: "border-box",
      background: "rgba(0,0,0,0.25)",
      overflow: "hidden",
    }}
  >
    <img
      src={image}
      alt=""
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      }}
      onError={(e) => {
        e.currentTarget.parentElement.style.display = "none";
      }}
    />
  </div>
)}

                  <div style={{ padding: 14 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          borderRadius: 999,
                          padding: "4px 10px",
                          background: "rgba(255,223,95,0.16)",
                          border: "1px solid rgba(255,223,95,0.35)",
                          color: "#ffdf5f",
                          fontSize: 12,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          textShadow: "1px 1px rgba(0,0,0,0.65)",
                        }}
                      >
                        {post.is_pinned ? "★ " : ""}
                        {categoryLabel(post.category)}
                      </span>

                      {post.published_at && (
                        <span
                          style={{
                            color: "rgba(255,255,255,0.68)",
                            fontSize: 13,
                            textShadow: "1px 1px rgba(0,0,0,0.65)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDate(post.published_at)}
                        </span>
                      )}
                    </div>

                    <div
                    className="shadow1"
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#fff",
                      }}
                    >
                      {post.title}
                    </div>

                    {post.summary && (
                      <div
                        style={{
                          marginTop: 8,
                          color: "rgba(255,255,255,0.86)",
                          fontSize: 16,
                          lineHeight: "21px",
                          textShadow: "1px 1px rgba(0,0,0,0.65)",
                        }}
                      >
                        {post.summary}
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: 12,
                        color: "#ffdf5f",
                        fontSize: 15,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        textShadow: "1px 1px rgba(0,0,0,0.7)",
                      }}
                    >
                      Read more →
                    </div>
                  </div>
                </motion.button>
              );
            })}
        </div>
      </div>

      <BlogPostModal
        post={selectedPost}
        onClose={() => setSelectedPost(null)}
        onNavigate={onNavigate}
        rewardStatus={
          selectedPost?.slug ? articleRewardStatus[selectedPost.slug] : null
        }
        claiming={selectedPost?.slug === claimingArticleSlug}
        onClaimReward={() => claimBlogArticleReward(selectedPost)}
      />
    </div>
  );
}